/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Fab,
  IconButton,
  Paper,
  Tooltip,
  Zoom,
  Grow,
} from '@mui/material';
import {
  Assistant as AssistantIcon,
  AssistantOutlined as AssistantOutlinedIcon,
} from '@mui/icons-material';
import { ChatPanel } from './ChatPanel';

const PANEL_WIDTH = 500;
const PANEL_HEIGHT = 1500;
const EDGE_SPACING = 24;

const panelStyle = {
  display: 'flex',
  flexDirection: 'column',
  position: 'fixed',
  bottom: EDGE_SPACING,
  right: EDGE_SPACING,
  width: PANEL_WIDTH,
  maxWidth: 'calc(100vw - 32px)',
  height: PANEL_HEIGHT,
  maxHeight: 'calc(100vh - 72px)',
  borderRadius: '12px',
  overflow: 'hidden',
  bgcolor: 'rgba(26, 32, 44, 0.75)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: 'rgba(0, 0, 0, 0.35) 0px 8px 24px',
};

export const ChatbotSidePanel = (props) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    // Disable listeners while closed to avoid a global click listener
    <ClickAwayListener
      onClickAway={handleClose}
      mouseEvent={open ? 'onClick' : false}
      touchEvent={open ? 'onTouchEnd' : false}
    >
      <Box>
        <Zoom in={!open} unmountOnExit>
          <Tooltip title="Open Assistant" placement="left">
            <Fab
              onClick={handleOpen}
              aria-label="Open EPS Assistant"
              sx={{
                position: 'fixed',
                bottom: EDGE_SPACING,
                right: EDGE_SPACING,
                zIndex: (theme) => theme.zIndex.modal + 1,
                bgcolor: '#84D8C5',
                '&:hover': { bgcolor: '#84D8C5' },
              }}
            >
              <AssistantOutlinedIcon sx={{ fill: '#1A202C !important' }} />
            </Fab>
          </Tooltip>
        </Zoom>

        {/* mountOnEnter defers the first mount until actually opened; no
        unmountOnExit keeps the session alive on every close after */}
        <Grow
          in={open}
          mountOnEnter
          style={{ transformOrigin: 'bottom right' }}
        >
          <Paper
            elevation={0}
            sx={{ ...panelStyle, zIndex: (theme) => theme.zIndex.modal + 1 }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1 }}
            >
              <Tooltip title="Close Assistant">
                <IconButton
                  onClick={handleClose}
                  aria-label="Close EPS Assistant"
                  sx={{
                    bgcolor: '#84D8C5',
                    '&:hover': { bgcolor: '#84D8C5' },
                  }}
                >
                  <AssistantIcon sx={{ fill: '#1A202C !important' }} />
                </IconButton>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                px: 2,
                pb: 2,
              }}
            >
              {props.children ?? <ChatPanel variant="sidePanel" />}
            </Box>
          </Paper>
        </Grow>
      </Box>
    </ClickAwayListener>
  );
};
